import { useLayoutEffect, useState, type ReactNode } from 'react'
import { Text, type TextProps } from '../Text/Text'
import { Tooltip } from '../Tooltip/Tooltip'

const TOOLTIP_MAX_WIDTH = 220

export interface OverflowTooltipProps extends Pick<TextProps, 'variant' | 'color'> {
  text: string
  /** 自訂 tooltip 內容（如同時顯示名稱與規格）；未指定時顯示 text 全文 */
  tooltipContent?: ReactNode
}

/**
 * 單行文字，內容過長時自動 ellipsis 截斷，並在 hover 時才以 tooltip 顯示完整內容；
 * 內容塞得下時為純文字、不顯示 tooltip。
 *
 * ⚠️ 寬度由外層容器決定（本元件以 width:100% 貼合父層），因此**外層必須有界定寬度**才會截斷：
 * - 表格欄位設 `size`、或外層 `width` / `maxWidth` → 可運作
 * - 放在 flex 容器內 → 需在 wrapper 加 `min-width: 0`，否則 flex item 不會縮、不會截斷
 * - 外層無任何限寬（auto 撐開）→ 退化為純文字、不截斷也不出 tooltip
 */
export const OverflowTooltip = ({ text, variant, color, tooltipContent }: OverflowTooltipProps) => {
  // callback ref：Tooltip 條件渲染翻轉造成 Text remount 時，才能重掛 observer 到新節點
  const [textElement, setTextElement] = useState<HTMLElement | null>(null)
  const [isTextOverflowing, setIsTextOverflowing] = useState(false)

  useLayoutEffect(function detectTextOverflow() {
    if (!textElement) return undefined

    const updateOverflow = () => setIsTextOverflowing(textElement.scrollWidth > textElement.clientWidth)
    updateOverflow()

    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(updateOverflow)
    observer.observe(textElement)
    return () => observer.disconnect()
  }, [textElement, text])

  const ellipsisText = (
    <Text ellipsis width="100%" variant={variant} color={color} rootRef={setTextElement}>
      {text}
    </Text>
  )

  if (!isTextOverflowing) return ellipsisText

  return (
    <Tooltip
      title={tooltipContent ?? text}
      placement="top"
      variant="dark"
      disablePortal={false}
      PaperProps={{ maxWidth: TOOLTIP_MAX_WIDTH }}
    >
      {ellipsisText}
    </Tooltip>
  )
}
